// src/components/LoginForm.js
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  TextField,
  Button,
  Container,
  CircularProgress,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { loginUser } from "../redux/authSlice";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { logger } from "../utils/logger";

const LoginForm = () => {
  const dispatch = useDispatch();
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    logger.info("Login form opened");
    return () => {
      logger.info("Login form closed");
    };
  }, []);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
  };

  // Handle login submit
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!credentials.username || !credentials.password) {
      const validationMsg = "Username and password are required.";
      setError(validationMsg);
      logger.warn("Login validation error", { credentials });
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await dispatch(loginUser(credentials)).unwrap();
      // Store tokens in sessionStorage
      if (result.token) {
        sessionStorage.setItem("token", result.token);
      }
      if (result.refreshToken) {
        sessionStorage.setItem("refreshToken", result.refreshToken);
      }
      logger.info("Login successful", {
        username: result.username,
        role: result.role,
      });
      // Navigation is handled by parent via Redux state
    } catch (error) {
      const backendMsg =
        error?.message ||
        error?.error ||
        (typeof error === "string" ? error : null) ||
        "Login failed. Please check your credentials.";
      setError(backendMsg);
      logger.error("Login error", {
        username: credentials.username,
        error: backendMsg,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => setError("");

  return (
    <Container maxWidth="xs">
      <form onSubmit={handleLogin}>
        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          name="username"
          label="Username"
          value={credentials.username}
          onChange={handleChange}
          autoFocus
          autoComplete="username"
        />
        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          name="password"
          label="Password"
          type={showPassword ? "text" : "password"}
          value={credentials.password}
          onChange={handleChange}
          autoComplete="current-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                  data-log="Show/Hide Password Icon Button"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : "Login"}
        </Button>
        {/* Forgot Password Link */}
        <div style={{ textAlign: "right", marginTop: 8 }}>
          <Link
            to="/forgot-password"
            style={{
              textDecoration: "none",
              color: "#1976d2",
              fontSize: "0.95rem",
            }}
          >
            Forgot Password?
          </Link>
        </div>
      </form>
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default LoginForm;
